import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import type { SupportedLanguage } from "./types";
import { en } from "./locales/en";
import { zh } from "./locales/zh";

/**
 * Default language used as fallback when browser language detection fails
 */
export const defaultLanguage: SupportedLanguage = "en";

/**
 * Array of all supported language codes
 * Used for language switching UI and validation
 */
export const supportedLanguages = ["en", "zh"] as const;

/**
 * Language display names for language switcher UI
 */
export const languageNames: Record<SupportedLanguage, string> = {
  en: "English",
  zh: "中文",
};

/**
 * Translation resources organized by language and namespace
 * Each language has a 'translation' namespace containing all translation keys
 */
const resources = {
  en: { translation: en },
  zh: { translation: zh },
} as const;

/**
 * Detects the user's preferred language from browser settings
 * Falls back to English for unsupported languages
 */
function detectBrowserLanguage(): SupportedLanguage {
  const browserLang = navigator.language.toLowerCase();

  // Check if browser language starts with 'zh' (includes zh-CN, zh-TW, etc.)
  if (browserLang.startsWith("zh")) {
    return "zh";
  }

  // Default to English for all other languages
  return "en";
}

/**
 * Get stored language preference from localStorage
 * Returns null if no preference is stored
 */
function getStoredLanguage(): SupportedLanguage | null {
  const stored = localStorage.getItem("scaffold-language");
  if (stored && supportedLanguages.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage;
  }
  return null;
}

/**
 * Store language preference in localStorage
 */
export function setStoredLanguage(language: SupportedLanguage): void {
  localStorage.setItem("scaffold-language", language);
}

/**
 * Initializes i18next with React integration
 * Should be called before rendering the React app
 *
 * Configuration details:
 * - Uses stored language preference or detects browser language automatically
 * - Falls back to English if detection fails
 * - Disables suspense to prevent loading flicker
 * - Uses React's built-in escaping for XSS prevention
 *
 * @returns The initialized i18n instance
 */
export function initI18n(): typeof i18n {
  const storedLang = getStoredLanguage();
  const detectedLang = storedLang || detectBrowserLanguage();

  i18n.use(initReactI18next).init({
    resources,
    lng: detectedLang,
    fallbackLng: defaultLanguage,
    defaultNS: "translation",
    ns: ["translation"],
    interpolation: {
      // React already handles escaping values, so we disable it in i18next
      escapeValue: false,
    },
    react: {
      // Disable Suspense to avoid loading state flicker
      useSuspense: false,
      // Bind i18n instance to React context for proper hook integration
      bindI18n: "languageChanged",
      bindI18nStore: "added removed",
    },
  });

  return i18n;
}

export default i18n;
